import React, { useMemo, useEffect, useState, useRef, useContext } from "react";
import ReactModal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import { axiosget, axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import FormSelect from "../../components/ui/FormSelect";
import is_money from "../../helpers/is_money";
import { contextapi } from "../../helpers/contextapi";
import Swal from "sweetalert2";
import { FiDollarSign, FiX, FiCreditCard, FiExternalLink, FiGift, FiArrowLeft, FiAlertTriangle, FiInfo } from "react-icons/fi";
import { toast } from "react-toastify";

const BtnPaymentGateway = ({ inline = false, quickApplySessionId }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedProgramme, setSelectedProgramme] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [showWaiver, setShowWaiver] = useState(false);
    const [waiverCodes, setWaiverCodes] = useState(["", "", "", "", "", ""]);
    const [isApplyingWaiver, setIsApplyingWaiver] = useState(false);
    const waiverInputRefs = useRef([]);
    const context = useContext(contextapi);
    const queryClient = useQueryClient();
    const [user, setUser] = useState(null);

    useEffect(() => {
        const { user: usrInfo } = context.values;
        setUser(usrInfo);
    }, [context.values]);

    // Fetch available programmes
    const { data: programmes } = useQuery({
        queryKey: ["paymentProgramList"],
        queryFn: async () => {
            const req = await axiosget("ms_programme");
            return req?.data;
        },
    });

    // Fetch default payment gateway
    const { data: gateway } = useQuery({
        queryKey: ["defaultPaymentGateway"],
        queryFn: async () => {
            const req = await axiosget("ms_payments/gateway");
            return req?.data?.data;
        },
    });

    // Transform programmes into select options
    const programmeOptions = useMemo(() => {
        if (!programmes || programmes?.type !== undefined) return [];
        return programmes.map((r) => ({
            value: r?._id,
            label: `${r.code} - ${r.name}`,
        }));
    }, [programmes]);

    // Fetch fee items for selected programme
    const {
        data: feeItems,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["paymentCost", selectedProgramme],
        queryFn: async () => {
            const req = await axiosget(`ms_application_fee_items/${selectedProgramme}`);
            return req?.data;
        },
        enabled: !!selectedProgramme,
    });

    // Calculate total amount
    const amount = useMemo(() => {
        if (!feeItems || feeItems?.type !== undefined) return 0;
        return feeItems.reduce((sum, item) => sum + item.amount, 0);
    }, [feeItems]);

    // Initialize payment through backend
    const handleInitiatePayment = async () => {
        try {
            setIsProcessing(true);

            // Get user from context or localStorage
            const currentUser = user || JSON.parse(localStorage.getItem("isApplicantData") || "{}");

            if (!currentUser?.firstname && !currentUser?.emailaddress && !currentUser?._id) {
                toast.error("Sorry, system could not find your profile information. Please log in again.");
                setIsProcessing(false);
                return;
            }

            if (!selectedProgramme) {
                toast.error("Please select a programme first");
                setIsProcessing(false);
                return;
            }

            const course = programmes?.find((r) => r._id === selectedProgramme);

            if (!course) {
                toast.error("Programme not found");
                setIsProcessing(false);
                return;
            }

            // Call backend to initialize payment
            const response = await axiospost("ms_payments/initialize", {
                feeType: "admission_fee",
                amount: amount,
                payerEmail: currentUser?.emailaddress || currentUser?.email,
                payerName: `${currentUser?.firstname || ""} ${currentUser?.middlename || ""} ${currentUser?.lastname || ""}`.trim() || currentUser?.name || "Applicant",
                payerPhone: currentUser?.phonenumber || currentUser?.phone,
                applicantId: currentUser?._id,
                metadata: {
                    programmeId: course._id,
                    programmeCode: course.code,
                    programmeName: course.name,
                    ...(quickApplySessionId && { quickApplySessionId }),
                },
            });

            setIsProcessing(false);

            if (!response?.data?.success) {
                swal("error", response?.data?.message || "Failed to initialize payment");
                return;
            }

            const paymentData = response.data.data;

            // Refresh payment list
            queryClient.invalidateQueries({ queryKey: ["generatedRRR"] });
            queryClient.invalidateQueries({ queryKey: ["applicantTransactions"] });
            queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
            setShowModal(false);

            // Show success and redirect
            const result = await Swal.fire({
                icon: "success",
                title: "Payment Initiated!",
                html: `
          <div class="text-center">
            <p class="text-neutral-600 mb-4">Your payment has been initiated via <strong>${gateway?.displayName || paymentData.gateway}</strong></p>
            ${paymentData.gatewayReference ? `
              <div class="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 mb-4">
                <p class="text-sm font-medium text-primary-900 mb-2">Payment Reference</p>
                <p class="text-xl font-bold text-primary-600 font-mono">${paymentData.gatewayReference}</p>
              </div>
            ` : ""}
            <p class="text-sm text-neutral-500">Click the button below to complete payment</p>
          </div>
        `,
                confirmButtonText: "Proceed to Payment",
                showCancelButton: true,
                cancelButtonText: "Pay Later",
                customClass: {
                    confirmButton: "bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg",
                    cancelButton: "bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-2 px-6 rounded-lg ml-2",
                },
            });

            if (result.isConfirmed && paymentData.paymentUrl) {
                window.open(paymentData.paymentUrl, "_blank");
            }
        } catch (error) {
            setIsProcessing(false);
            swal("error", error?.message || "Failed to initialize payment. Please try again.");
            console.error("Payment Initialization Error:", error);
        }
    };

    const handleWaiverCodeChange = (index, value) => {
        const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        if (sanitized.length <= 1) {
            const next = [...waiverCodes];
            next[index] = sanitized;
            setWaiverCodes(next);
            if (sanitized && index < 5) waiverInputRefs.current[index + 1]?.focus();
        }
    };

    const handleWaiverKeyDown = (index, e) => {
        if (e.key === "Backspace" && !waiverCodes[index] && index > 0) {
            waiverInputRefs.current[index - 1]?.focus();
        }
    };

    const handleWaiverPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
        const next = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
        setWaiverCodes(next);
        const nextEmpty = next.findIndex(c => !c);
        waiverInputRefs.current[nextEmpty !== -1 ? nextEmpty : 5]?.focus();
    };

    const handleApplyWaiver = async () => {
        const waiverCode = waiverCodes.join("");
        if (waiverCode.length !== 6) {
            toast.error("Please enter the full 6-character waiver code");
            return;
        }
        if (!selectedProgramme) {
            toast.error("Please select a programme first");
            return;
        }
        try {
            setIsApplyingWaiver(true);
            const res = await axiospost("ms_application_fee/waiver-direct", {
                waiverCode,
                programmeId: selectedProgramme,
                amount,
            });
            setIsApplyingWaiver(false);
            if (res?.data?.type === "success") {
                queryClient.invalidateQueries({ queryKey: ["generatedRRR"] });
                queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
                setShowModal(false);
                setShowWaiver(false);
                setWaiverCodes(["", "", "", "", "", ""]);
                swal("success", res.data.message);
            } else {
                swal("error", res?.data?.message || "Failed to apply waiver");
            }
        } catch (error) {
            setIsApplyingWaiver(false);
            swal("error", error?.message || "Failed to apply waiver");
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setShowWaiver(false);
        setWaiverCodes(["", "", "", "", "", ""]);
    };

    return (
        <div>
            <ReactModal
                isOpen={showModal}
                onRequestClose={closeModal}
                className="ReactModal"
                overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl min-h-[500px] max-h-[90vh] overflow-hidden flex flex-col"
                    role="document"
                >
                    {/* Header */}
                    <div className={`relative px-6 py-5 rounded-t-xl bg-gradient-to-r ${showWaiver ? "from-amber-500 via-amber-600 to-orange-600" : "from-primary-600 via-primary-700 to-primary-800"}`}>
                        <div className="flex items-center gap-3">
                            {showWaiver && (
                                <button
                                    onClick={() => { setShowWaiver(false); setWaiverCodes(["", "", "", "", "", ""]); }}
                                    className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                                    aria-label="Back"
                                >
                                    <FiArrowLeft className="h-5 w-5 text-white" />
                                </button>
                            )}
                            <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                                {showWaiver ? <FiGift className="h-6 w-6 text-white" /> : <FiDollarSign className="h-6 w-6 text-white" />}
                            </div>
                            <div className="flex-1">
                                <h5 className="text-xl font-semibold text-white">
                                    {showWaiver ? "Apply Waiver Code" : "Pay Application Fee"}
                                </h5>
                                <p className="text-white/80 text-sm mt-0.5">
                                    {showWaiver ? "Enter your 6-character waiver code to bypass payment" : gateway ? (
                                        <>Using <strong>{gateway.displayName}</strong> ({gateway.environment} mode)</>
                                    ) : (
                                        "Select programme and make payment"
                                    )}
                                </p>
                            </div>
                            <button
                                onClick={closeModal}
                                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                                aria-label="Close modal"
                            >
                                <FiX className="h-6 w-6 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 overflow-y-auto flex-1">
                        {!gateway && (
                            <Alert variant="warning" className="mb-4">
                                <AlertDescription>
                                    No payment gateway is configured. Please contact the administrator.
                                </AlertDescription>
                            </Alert>
                        )}

                        <Formik
                            initialValues={{ programme: "" }}
                            onSubmit={() => { }}
                        >
                            {({ values }) => {
                                // Observer component to sync Formik value with state
                                const FormikValuesObserver = () => {
                                    useEffect(() => {
                                        if (values.programme !== selectedProgramme) {
                                            setSelectedProgramme(values.programme);
                                        }
                                    }, [values.programme]);
                                    return null;
                                };

                                return (
                                    <Form className="space-y-6">
                                        <FormikValuesObserver />
                                        <FormSelect
                                            name="programme"
                                            label="Select Programme You Are Applying For"
                                            options={programmeOptions}
                                            required
                                            searchable
                                            clearable
                                            helperText="Search or scroll to find your programme"
                                        />

                                        {isLoading && (
                                            <div className="mt-4">
                                                <FormSkeleton fields={1} />
                                            </div>
                                        )}

                                        {error && (
                                            <Alert variant="danger">
                                                <AlertDescription>
                                                    Error: {error?.message || error}
                                                </AlertDescription>
                                            </Alert>
                                        )}

                                        {feeItems?.type !== undefined && (
                                            <Alert variant="info">
                                                <AlertDescription>{feeItems?.message}</AlertDescription>
                                            </Alert>
                                        )}

                                        {feeItems && feeItems?.type === undefined && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 }}
                                                className="space-y-4"
                                            >
                                                <div className="bg-neutral-50 rounded-lg border border-neutral-200 overflow-hidden">
                                                    <table className="w-full">
                                                        <thead className="bg-neutral-100 border-b border-neutral-200">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">SN</th>
                                                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">Title</th>
                                                                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-neutral-200">
                                                            {feeItems?.map((r, i) => (
                                                                <tr key={`payment_items_${i}${r?._id}`} className="hover:bg-neutral-50 transition-colors">
                                                                    <td className="px-4 py-3 text-sm font-medium text-neutral-900">{i + 1}</td>
                                                                    <td className="px-4 py-3 text-sm text-neutral-700">{r.name}</td>
                                                                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 text-right">{is_money(r.amount)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-primary-50 border-t-2 border-primary-200">
                                                            <tr>
                                                                <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-primary-900">Total Amount</td>
                                                                <td className="px-4 py-3 text-lg font-bold text-primary-900 text-right">{is_money(amount)}</td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>

                                                <div className="pt-4 border-t border-neutral-200 space-y-3">
                                                    <AnimatePresence mode="wait">
                                                        {!showWaiver ? (
                                                            <motion.div
                                                                key="pay-buttons"
                                                                initial={{ opacity: 0, y: 6 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -6 }}
                                                                transition={{ duration: 0.15 }}
                                                                className="flex gap-2"
                                                            >
                                                                <Button
                                                                    variant="primary"
                                                                    size="lg"
                                                                    fullWidth
                                                                    onClick={handleInitiatePayment}
                                                                    loading={isProcessing}
                                                                    disabled={isProcessing || !gateway}
                                                                    rightIcon={!isProcessing && <FiExternalLink />}
                                                                >
                                                                    {isProcessing
                                                                        ? "Processing..."
                                                                        : `Pay ${is_money(amount)} via ${gateway?.displayName || "Gateway"}`}
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="lg"
                                                                    fullWidth
                                                                    onClick={() => { setShowWaiver(true); setTimeout(() => waiverInputRefs.current[0]?.focus(), 100); }}
                                                                    leftIcon={<FiGift className="h-4 w-4" />}
                                                                >
                                                                    Pay with Waiver
                                                                </Button>
                                                            </motion.div>
                                                        ) : (
                                                            <motion.div
                                                                key="waiver-panel"
                                                                initial={{ opacity: 0, y: 6 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -6 }}
                                                                transition={{ duration: 0.15 }}
                                                                className="space-y-4"
                                                            >
                                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                                    <div className="flex items-start gap-2">
                                                                        <FiInfo className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                                                        <p className="text-sm text-blue-800">
                                                                            Enter your 6-character waiver code. It must match the fee amount of <strong>{is_money(amount)}</strong>.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                                                                    <span className="text-sm font-medium text-amber-900">Application Fee</span>
                                                                    <span className="text-lg font-bold text-neutral-900">{is_money(amount)}</span>
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-neutral-700 text-center mb-3">
                                                                        Enter Waiver Code
                                                                    </label>
                                                                    <div className="flex justify-center gap-2">
                                                                        {waiverCodes.map((code, index) => (
                                                                            <input
                                                                                key={index}
                                                                                ref={el => (waiverInputRefs.current[index] = el)}
                                                                                type="text"
                                                                                maxLength="1"
                                                                                value={code}
                                                                                onChange={e => handleWaiverCodeChange(index, e.target.value)}
                                                                                onKeyDown={e => handleWaiverKeyDown(index, e)}
                                                                                onPaste={handleWaiverPaste}
                                                                                className="w-11 h-13 text-center text-xl font-bold font-mono uppercase rounded-lg border-2 border-neutral-300 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all bg-white text-neutral-900"
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2">
                                                                    <Button
                                                                        variant="primary"
                                                                        size="lg"
                                                                        fullWidth
                                                                        onClick={handleApplyWaiver}
                                                                        loading={isApplyingWaiver}
                                                                        disabled={isApplyingWaiver || waiverCodes.join("").length !== 6}
                                                                        leftIcon={!isApplyingWaiver && <FiGift className="h-4 w-4" />}
                                                                        className="bg-amber-500 hover:bg-amber-600 border-amber-500 hover:border-amber-600"
                                                                    >
                                                                        {isApplyingWaiver ? "Applying..." : "Apply Waiver"}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="lg"
                                                                        onClick={() => { setShowWaiver(false); setWaiverCodes(["", "", "", "", "", ""]); }}
                                                                    >
                                                                        Back
                                                                    </Button>
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            </motion.div>
                                        )}
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </motion.div>
            </ReactModal>

            {inline ? (
                <div className="flex justify-end mb-3">
                    <Button
                        variant="primary"
                        onClick={() => setShowModal(true)}
                        leftIcon={<FiCreditCard className="h-4 w-4" />}
                    >
                        Generate Payment
                    </Button>
                </div>
            ) : (
                /* Modern FAB with Gateway indicator */
                gateway && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        className="fixed right-6 bottom-24 md:right-12 md:bottom-28 z-40"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowModal(true)}
                            className="h-14 px-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center gap-2 group"
                            aria-label="Pay with Gateway"
                        >
                            <FiCreditCard className="h-5 w-5" />
                            <span className="text-sm font-medium">Pay Now</span>
                        </motion.button>
                    </motion.div>
                )
            )}
        </div>
    );
};

export default BtnPaymentGateway;
